/** Bottom nav tab identity — Stage 3 fixed primary order (retail shell). */

import {
  clearPlatformReturnTarget,
  clearPostAuthOrdersIntent,
  peekPlatformReturnTarget,
} from '../shared/session/returnTarget.js';

export type BottomNavTab =
  | 'browse'
  | 'shop'
  | 'scan'
  | 'orders'
  | 'orgs'
  | 'shops'
  | 'receipts'
  | 'promo'
  | 'account'
  | 'overview'
  | 'more'
  | 'donate'
  | 'recurring'
  | 'my-donations';

/** RR location.state key when a protected tab gates through sign-in. */
export const BOTTOM_NAV_AUTH_INTENT_STATE_KEY = 'bottomNavAuthIntent';

/**
 * In-memory auth-gate intent — survives same-URL `/sign-in` re-clicks when
 * React Router would otherwise keep a stale `location.state` (shop vs orders
 * both gate to the same href). Cleared when leaving auth chrome.
 *
 * Only protected primary tabs that gate through sign-in (orders / Oblíbené /
 * Více). Nakupovat (`browse`) is public — never stored here.
 */
let memoryAuthIntentTab: BottomNavTab | null = null;

export function setBottomNavMemoryAuthIntent(tab: BottomNavTab | null): void {
  memoryAuthIntentTab =
    tab === 'orders' || tab === 'shop' || tab === 'more' ? tab : null;
}

export function peekBottomNavMemoryAuthIntent(): BottomNavTab | null {
  return memoryAuthIntentTab;
}

export function clearBottomNavMemoryAuthIntent(): void {
  memoryAuthIntentTab = null;
}

function isOrdersPlatformReturnTarget(pathWithQuery: string): boolean {
  const pathOnly =
    pathWithQuery.indexOf('?') === -1
      ? pathWithQuery
      : pathWithQuery.slice(0, pathWithQuery.indexOf('?'));
  return (
    pathOnly === '/account/orders' || pathOnly.startsWith('/account/orders/')
  );
}

/**
 * Drop guest Objednávky auth-gate stickiness when sign-in is cancelled, fails,
 * or the user navigates away without success. Peers with RequireCustomerAuth
 * orders park + BottomNav guest orders tap.
 */
export function clearOrdersAuthGateIntent(): void {
  clearBottomNavMemoryAuthIntent();
  clearPostAuthOrdersIntent();
  const platformPark = peekPlatformReturnTarget();
  if (platformPark !== null && isOrdersPlatformReturnTarget(platformPark)) {
    clearPlatformReturnTarget();
  }
}

function tenantRelativePath(pathname: string, tenantCode: string): string {
  const prefix = `/${encodeURIComponent(tenantCode)}`;
  if (pathname === prefix) {
    return '/';
  }
  if (pathname.startsWith(`${prefix}/`)) {
    const rest = pathname.slice(prefix.length);
    return rest.length > 0 ? rest : '/';
  }
  return pathname;
}

export { tenantRelativePath };

/**
 * True for shop / scan / home retail paths that own the live cart sales point.
 * Nakupovat (`/browse`) is discovery chrome — not a cart owner; exclude it.
 */
export function isRetailShopOrScanPath(rel: string): boolean {
  return (
    rel === '/shop' ||
    rel.startsWith('/shop/') ||
    rel === '/account/shop' ||
    rel.startsWith('/account/shop/') ||
    rel === '/scan' ||
    rel.startsWith('/scan/') ||
    rel === '/home' ||
    rel.startsWith('/home/')
  );
}

/** Bottom nav stays on auth/login routes; recovery + contact-verification interstitials hide it. */
export function shouldHideBottomNav(
  pathname: string,
  tenantCode: string,
  options?: { readonly mustCompleteVerification?: boolean },
): boolean {
  if (options?.mustCompleteVerification === true) {
    return true;
  }
  const rel = tenantRelativePath(pathname, tenantCode);
  if (
    rel === '/account/complete-verification' ||
    rel.startsWith('/account/complete-verification/') ||
    rel === '/account/add-phone' ||
    rel.startsWith('/account/add-phone/')
  ) {
    return true;
  }
  return rel === '/recovery' || rel.startsWith('/recovery/');
}

function isAccountOverflowRoute(rel: string): boolean {
  const normalized =
    rel.length > 1 && rel.endsWith('/') ? rel.slice(0, -1) : rel;
  return (
    normalized === '/account' ||
    normalized === '/account/home' ||
    normalized.startsWith('/account/donate') ||
    normalized.startsWith('/account/recurring-donations') ||
    normalized.startsWith('/account/donations')
  );
}

/** Sign-in / register / password chrome — bottom nav stays mounted (guest gate). */
export function isBottomNavAuthChromePath(pathname: string, tenantCode: string): boolean {
  if (
    pathname === '/sign-in' ||
    pathname.startsWith('/sign-in/') ||
    pathname === '/sign-up' ||
    pathname.startsWith('/sign-up/')
  ) {
    return true;
  }
  const rel = tenantRelativePath(pathname, tenantCode);
  return (
    rel === '/sign-in' ||
    rel.startsWith('/sign-in/') ||
    rel === '/sign-up' ||
    rel.startsWith('/sign-up/') ||
    rel === '/forgot-password' ||
    rel.startsWith('/forgot-password/') ||
    rel === '/reset-password' ||
    rel.startsWith('/reset-password/') ||
    rel === '/confirm-email' ||
    rel.startsWith('/confirm-email/') ||
    rel === '/card/link' ||
    rel.startsWith('/card/link/') ||
    rel.startsWith('/onboarding/')
  );
}

function pathOnly(pathWithQuery: string): string {
  const q = pathWithQuery.indexOf('?');
  return q === -1 ? pathWithQuery : pathWithQuery.slice(0, q);
}

function tabFromReturnTarget(pathWithQuery: string, tenantCode: string): BottomNavTab | null {
  const path = pathOnly(pathWithQuery);
  // Avoid recursion through auth chrome — resolve the destination path only.
  if (isBottomNavAuthChromePath(path, tenantCode)) {
    return null;
  }
  const tab = resolveBottomNavActiveTab(path, tenantCode);
  // Protected tabs only — Nakupovat (`browse`) is public; never lit on auth chrome.
  if (tab === 'orders' || tab === 'shop' || tab === 'more') {
    return tab;
  }
  return null;
}

export interface ResolveBottomNavActiveTabOptions {
  /** From NavLink location.state when gating a protected tab through sign-in. */
  readonly authIntentTab?: BottomNavTab | null;
  /** Peeked return-target path (orders/shop) while on auth chrome. */
  readonly returnTargetPath?: string | null;
  /** Peeked platform post-auth orders intent flag. */
  readonly hasOrdersAuthIntent?: boolean;
}

/** True when the URL is (or ends with) an account orders surface — tenant-agnostic. */
function isAccountOrdersPathname(pathname: string): boolean {
  return /(?:^|\/)account\/orders(?:\/|$)/.test(pathname);
}

/** True when the URL is an account shop surface — tenant-agnostic. */
function isAccountShopPathname(pathname: string): boolean {
  return /(?:^|\/)account\/shop(?:\/|$)/.test(pathname);
}

/** True when the URL is platform Nakupovat — tenant-agnostic (wrong TenantProvider safe). */
function isBrowsePathname(pathname: string): boolean {
  return /(?:^|\/)browse(?:\/|$)/.test(pathname);
}

/** D-03 / D-04 — deterministic active tab from URL (+ auth-gate intent). */
export function resolveBottomNavActiveTab(
  pathname: string,
  tenantCode: string,
  options?: ResolveBottomNavActiveTabOptions,
): BottomNavTab {
  // Platform Organizace (`/orgs`) — tab id kept for href builder; no primary bar item.
  if (pathname === '/orgs' || pathname.startsWith('/orgs/')) {
    return 'orgs';
  }
  // Platform Nakupovat (Stage 3 placeholder / Stage 4 discovery entry).
  if (isBrowsePathname(pathname)) {
    return 'browse';
  }
  // Platform /shops (and tenant alias) — public shops directory (mobile tab when showShops).
  if (pathname === '/shops' || pathname.startsWith('/shops/')) {
    return 'shops';
  }
  // Platform guest Oblíbené (starred shops hub).
  if (pathname === '/favorites' || pathname.startsWith('/favorites/')) {
    return 'shop';
  }
  // Platform Scan hub (and tenant alias).
  if (pathname === '/scan' || pathname.startsWith('/scan/')) {
    return 'scan';
  }

  if (isBottomNavAuthChromePath(pathname, tenantCode)) {
    const memoryIntent = peekBottomNavMemoryAuthIntent();
    if (
      memoryIntent === 'orders' ||
      memoryIntent === 'shop' ||
      memoryIntent === 'more'
    ) {
      return memoryIntent;
    }
    const intent = options?.authIntentTab;
    if (intent === 'orders' || intent === 'shop' || intent === 'more') {
      return intent;
    }
    const fromReturn =
      options?.returnTargetPath !== undefined &&
      options.returnTargetPath !== null &&
      options.returnTargetPath.length > 0
        ? tabFromReturnTarget(options.returnTargetPath, tenantCode)
        : null;
    if (fromReturn !== null) {
      return fromReturn;
    }
    if (options?.hasOrdersAuthIntent === true) {
      return 'orders';
    }
    // Auth gate in progress — do not pretend Oblíbené (shop) is the current page.
    return 'account';
  }

  // Tenant-agnostic safety net (wrong TenantProvider code must not mark shop).
  if (isAccountOrdersPathname(pathname)) {
    return 'orders';
  }
  if (isAccountShopPathname(pathname)) {
    return 'shop';
  }
  if (isBrowsePathname(pathname)) {
    return 'browse';
  }

  const rel = tenantRelativePath(pathname, tenantCode);

  if (isBrowsePathname(rel)) {
    return 'browse';
  }
  if (rel === '/shops' || rel.startsWith('/shops/')) {
    return 'shops';
  }
  if (rel === '/favorites' || rel.startsWith('/favorites/')) {
    return 'shop';
  }
  if (
    rel === '/scan' ||
    rel.startsWith('/scan/') ||
    rel === '/shop/scanner' ||
    rel.startsWith('/shop/scanner/') ||
    rel === '/self-scan' ||
    rel.startsWith('/self-scan/')
  ) {
    return 'scan';
  }
  if (rel.startsWith('/account/orders')) {
    return 'orders';
  }
  // Pending QR / payment claim stay under the Objednávky tab (unified history).
  if (rel.startsWith('/account/payments')) {
    return 'orders';
  }
  if (rel.startsWith('/account/shop')) {
    return 'shop';
  }
  if (rel.startsWith('/more') || rel.startsWith('/orders/find') || rel.startsWith('/help')) {
    return 'more';
  }
  // Profile chrome — Účet a soukromí (not a Více overflow destination).
  // Use legacy 'account' tab id so no primary bar item (and not Více) lights.
  if (
    rel.startsWith('/account/account-and-privacy') ||
    rel.startsWith('/account/privacy') ||
    rel.startsWith('/account/settings')
  ) {
    return 'account';
  }
  // Other Více overflow destinations mark the More tab.
  if (
    rel.startsWith('/checkout/complete') ||
    rel.startsWith('/account/donate') ||
    rel.startsWith('/account/recurring-donations') ||
    rel.startsWith('/account/donations') ||
    isAccountOverflowRoute(rel) ||
    rel.startsWith('/donate')
  ) {
    return 'more';
  }
  return 'shop';
}

/**
 * Canonical bottom-nav destinations — Wave D platform chrome SSOT.
 * Account / more hubs are bare `/account/**` and `/more` (no `/{jwt}/…`).
 * Scan always lands on platform `/scan`; `tenantCode` ignored for that tab.
 */
export function buildBottomNavHref(
  tab: BottomNavTab,
  _tenantCode: string,
  options?: { readonly shopHref?: string },
): string {
  switch (tab) {
    case 'browse':
      return '/browse';
    case 'shop':
      return options?.shopHref ?? '/account/shop';
    case 'scan':
      return '/scan';
    case 'orders':
      return '/account/orders';
    case 'orgs':
      return '/orgs';
    case 'shops':
      return '/shops';
    case 'receipts':
      // Legacy tab id — receipts live on Objednávky.
      return '/account/orders';
    case 'promo':
      // Legacy tab id — orphan surface removed; land on account shop.
      return '/account/shop';
    case 'account':
      return '/account/account-and-privacy';
    case 'overview':
      return '/account';
    case 'donate':
      return '/account/donate';
    case 'recurring':
      return '/account/recurring-donations';
    case 'my-donations':
      return '/account/donations';
    case 'more':
      return '/more';
  }
}
